import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, Money, useFocusHighlight, NotesBadge } from "../../components/shared";
import { CsvTools } from "../../components/CsvTools";
import { DataTable } from "../../components/DataTable";
import { PARTY_EXTRA } from "../../lib/extraColumns";
import { Button } from "../../components/ui/button";
import { usePerms } from "../../lib/perms";

export function Customers() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { rowProps } = useFocusHighlight();
  const { can } = usePerms();

  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/customers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Customer deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <>
      <SectionHeader title="Customers" subtitle="Accounts receivable">
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity="customers"
            queryKey={["customers"]}
            label="customers"
            rows={customers}
            columns={[
              { key: "code", label: "code", importKey: false },
              { key: "name", label: "name" },
              { key: "email", label: "email" },
              { key: "phone", label: "phone" },
              { key: "address", label: "address" },
              { key: "credit_limit", label: "credit_limit" },
              { key: "balance", label: "balance", importKey: false },
            ]}
          />
          {can("sales.edit") && <Button onClick={() => navigate("/sales/customer/new")} data-testid="new-customer-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><Plus size={16} weight="bold" className="mr-1" /> New Customer</Button>}
        </div>
      </SectionHeader>
      <DataTable
        testid="customers-table"
        data={customers}
        rowKey={(c) => c.id}
        rowTestid="customer-row"
        rowProps={(c) => rowProps(c.id)}
        empty="No customers yet."
        viewKey="customers" namedViews extraColumns={PARTY_EXTRA}
        columns={[
          { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs", cell: (c) => <button onClick={() => navigate(`/sales/customer/${c.id}`)} data-testid="open-customer" className="text-primary hover:underline">{c.code}</button> },
          { key: "name", header: "Name", filter: "text", cellClassName: "text-sm font-medium", cell: (c) => <button onClick={() => navigate(`/sales/customer/${c.id}`)} className="text-left hover:underline inline-flex items-center gap-1.5">{c.name}<NotesBadge count={c.notes?.length} testid="customer-notes-count" /></button> },
          { key: "email", header: "Contact", filter: "text", cellClassName: "text-sm text-muted-foreground" },
          { key: "credit_limit", header: "Credit Limit", align: "right", numeric: true, cell: (c) => <Money value={c.credit_limit} className="text-sm text-muted-foreground" /> },
          { key: "balance", header: "Balance", align: "right", numeric: true, cell: (c) => <Money value={c.balance} className="text-sm font-medium" /> },
          { key: "actions", header: "", sortable: false, align: "right", cell: (c) => (
            <div className="flex justify-end gap-1">
              <button onClick={() => navigate(`/sales/customer/${c.id}`)} className="p-1.5 text-muted-foreground hover:text-primary" data-testid="edit-customer"><FileText size={16} /></button>
              {can("sales.delete") && <button onClick={() => del.mutate(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid="delete-customer"><Trash size={16} /></button>}
            </div>
          ) },
        ]}
      />
    </>
  );
}
